
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState, useCallback } from "react";

interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
  posStatus?: 'found' | 'not-found';
  catalogName?: string;
  createdAt?: string;
  suggestedRetailPrice?: number;
  costPerUnit?: number;
  square_item_id?: string;
  square_variation_id?: string;
  squareCategoryId?: string;
  mappedCategoryId?: string;
}

export const useOptimizedProductList = (orderId: string, loadAll: boolean = false) => {
  // Pagination state (only used when loadAll is false)
  const [currentPage, setCurrentPage] = useState(0);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0 });
  const pageSize = 30; // Optimal page size for performance
  
  // First, get total count to determine if we have a large dataset
  const { data: countData } = useQuery({
    queryKey: ['product-count', orderId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('order_id', orderId);
      
      if (error) throw error;
      return count;
    },
    enabled: !!orderId,
    staleTime: 300000 // Cache count for 5 minutes
  });

  // Optimized batch POS status checking
  const checkPosStatusBatch = async (products: any[]) => {
    if (!products || products.length === 0) return [];
    
    console.log('🔄 Batch checking POS status for', products.length, 'products');
    
    // Extract all unique barcodes
    const barcodes = [...new Set(products.map(p => p.barcode).filter(Boolean))];
    
    if (barcodes.length === 0) {
      return products.map(product => ({
        ...product,
        posStatus: 'not-found' as const,
        catalogName: undefined,
        squareCategoryId: undefined,
        mappedCategoryId: undefined
      }));
    }
    
    try {
      // Single batch query for all barcodes - now including category
      const { data: catalogData, error } = await supabase
        .from('product_catalog')
        .select('barcode, name, category')
        .in('barcode', barcodes);
      
      if (error) {
        console.error('Error batch checking POS status:', error);
        throw error;
      }
      
      // Get unique Square category IDs for mapping
      const squareCategoryIds = [...new Set(
        (catalogData || [])
          .map(item => item.category)
          .filter(Boolean)
      )];
      
      // Batch map Square categories to master categories
      const categoryMappings = new Map();
      if (squareCategoryIds.length > 0) {
        for (const squareCategoryId of squareCategoryIds) {
          try {
            const { data: mappedCategoryId } = await supabase
              .rpc('map_square_to_master_category', {
                input_square_category_id: squareCategoryId
              });
            if (mappedCategoryId) {
              categoryMappings.set(squareCategoryId, mappedCategoryId);
            }
          } catch (mappingError) {
            console.warn('Failed to map Square category:', squareCategoryId, mappingError);
          }
        }
      }
      
      // Create lookup map for O(1) access
      const catalogMap = new Map(
        (catalogData || []).map(item => [
          item.barcode, 
          {
            name: item.name,
            squareCategoryId: item.category,
            mappedCategoryId: item.category ? categoryMappings.get(item.category) : undefined
          }
        ])
      );
      
      // Map products with POS status
      const productsWithStatus = products.map(product => {
        const catalogInfo = catalogMap.get(product.barcode);
        return {
          ...product,
          posStatus: catalogInfo ? 'found' as const : 'not-found' as const,
          catalogName: catalogInfo?.name,
          squareCategoryId: catalogInfo?.squareCategoryId,
          mappedCategoryId: catalogInfo?.mappedCategoryId
        };
      });
      
      console.log('✅ Batch POS status check complete:', {
        total: products.length,
        found: productsWithStatus.filter(p => p.posStatus === 'found').length,
        notFound: productsWithStatus.filter(p => p.posStatus === 'not-found').length
      });
      
      return productsWithStatus;
      
    } catch (error) {
      console.error('Error in batch POS status check:', error);
      // Return products with not-found status as fallback
      return products.map(product => ({
        ...product,
        posStatus: 'not-found' as const,
        catalogName: undefined
      }));
    }
  };

  // Fetch products with conditional pagination and optimized POS checking
  const { data: pageData, isLoading: productsLoading, error: productsError, isFetching } = useQuery({
    queryKey: ['optimized-products', orderId, loadAll ? 'all' : currentPage],
    queryFn: async () => {
      console.log('🔄 Fetching products:', loadAll ? 'all products' : `page ${currentPage}`, 'for order:', orderId);
      
      try {
        let query = supabase
          .from('products')
          .select(`
            id,
            barcode,
            name,
            boxes,
            units_per_box,
            photos,
            has_credit,
            created_at,
            suggested_retail_price,
            cost_per_unit,
            square_item_id,
            square_variation_id
          `)
          .eq('order_id', orderId)
          .order('created_at', { ascending: false });

        // Apply pagination only if not loading all
        if (!loadAll) {
          query = query.range(currentPage * pageSize, (currentPage + 1) * pageSize - 1);
        }

        const { data, error } = await query;
        
        if (error) {
          console.error('❌ Error fetching products:', error);
          throw error;
        }
        
        console.log('✅ Products fetched:', data?.length || 0, loadAll ? '(all products)' : `(page ${currentPage})`);
        
        if (!data || data.length === 0) {
          return [];
        }
        
        // Update progress for UI feedback
        setLoadingProgress({ loaded: 0, total: data.length });
        
        // Batch check POS status for all products at once
        const productsWithPosStatus = await checkPosStatusBatch(data);
        
        // Final progress update
        setLoadingProgress({ loaded: data.length, total: data.length });
        
        return productsWithPosStatus;
        
      } catch (error) {
        console.error('❌ Products fetch failed:', error);
        throw error;
      }
    },
    enabled: !!orderId,
    retry: 2,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    gcTime: 300000
  });

  // Update all products when new page data arrives (only for paginated mode)
  useMemo(() => {
    if (loadAll) {
      // For loadAll mode, just use the data directly
      if (pageData) {
        setAllProducts(pageData);
      }
    } else {
      // For paginated mode, handle page accumulation
      if (pageData && currentPage === 0) {
        // First page - replace all products
        setAllProducts(pageData);
      } else if (pageData && currentPage > 0) {
        // Subsequent pages - append to existing products
        setAllProducts(prev => [...prev, ...pageData]);
      }
    }
  }, [pageData, currentPage, loadAll]);

  // Load more function for infinite scroll (only used in paginated mode)
  const loadMore = useCallback(() => {
    if (loadAll) return; // Don't load more if we're already loading all
    
    const totalCount = countData || 0;
    const hasMore = (currentPage + 1) * pageSize < totalCount;
    
    if (hasMore && !isFetching) {
      console.log('📖 Loading next page:', currentPage + 1);
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, countData, pageSize, isFetching, loadAll]);

  // Reset pagination when order changes
  useMemo(() => {
    setCurrentPage(0);
    setAllProducts([]);
  }, [orderId]);

  // Memoize the processed products to prevent unnecessary re-calculations
  const products = useMemo(() => {
    if (!Array.isArray(allProducts)) return [];
    
    console.log('🔄 Processing products data...', allProducts.length, 'products');
    
    // Transform products to expected format with stable references
    return allProducts.map(product => ({
      id: product.id,
      barcode: product.barcode,
      name: product.name,
      boxes: product.boxes,
      unitsPerBox: product.units_per_box,
      photos: product.photos || [],
      hasCredit: product.has_credit,
      posStatus: product.posStatus || 'not-found' as const,
      catalogName: product.catalogName,
      createdAt: product.created_at,
      suggestedRetailPrice: product.suggested_retail_price,
      costPerUnit: product.cost_per_unit,
      square_item_id: product.square_item_id,
      square_variation_id: product.square_variation_id,
      squareCategoryId: product.squareCategoryId,
      mappedCategoryId: product.mappedCategoryId
    }));
  }, [allProducts]);

  // Calculate if there are more products to load (only relevant for paginated mode)
  const totalCount = countData || 0;
  const hasMore = !loadAll && products.length < totalCount;

  // Show warning for large datasets
  const isLargeDataset = totalCount > 500;

  return {
    products,
    productsLoading: productsLoading && (loadAll || currentPage === 0), // Show loading for all products or first page
    productsError,
    hasMore,
    isFetchingNextPage: !loadAll && isFetching && currentPage > 0,
    loadMore: loadAll ? () => {} : loadMore, // Disable loadMore when loading all
    totalProducts: totalCount,
    isLargeDataset,
    currentPageSize: products.length,
    loadingProgress
  };
};
